/* Generated by restful-react */

import React from 'react'
import {
  Get,
  GetProps,
  useGet,
  UseGetProps,
  Mutate,
  MutateProps,
  useMutate,
  UseMutateProps,
} from 'restful-react'

export const SPEC_VERSION = 'v9'
export interface User {
  parentUserId: number
  firstName: string
  lastName: string
  email?: string
  phoneNumber?: string
}

export interface LaneAlert {
  id: number
  pickupCity: string
  pickupStateCode: string
  pickupMiles?: number | null
  pickupStartDate?: string | null
  pickupEndDate?: string | null
  deliveryCity: string | null
  deliveryStateCode: string | null
  deliveryMiles?: number | null
  loadedMilesMin?: number | null
  loadedMilesMax?: number | null
  sourceType: 'dispatcher_created' | 'cm_created' | null
  trailerType: 'dry_van' | 'reefer' | 'open_deck' | null
  dropTrailer: boolean | null
  createdAt: string
  updatedAt?: string
  pickupIsoDaysOfWeek?: number[]
  carrierApplication: {
    id?: number
    legalName?: string
    dotNumber?: string | null
    mcNumber?: string | null
  } | null
  pickupAvailability?: string[]
  recipientSubscribers: {
    parentUserId: number
    firstName: string
    lastName: string
    email?: string
    phoneNumber?: string
    hasNotificationsEnabled: boolean
    createdAt: string
    updatedAt: string
    createdBy?: User
  }[]
  createdBy: User
  rate?: number | null
  rateType?: '' | 'all_in' | 'rpm' | null
}

export interface LaneAlertResponse {
  laneAlerts: LaneAlert[]
}

export interface BookableShipmentSavedSearch {
  pickupCity?: string | null
  pickupStateCode?: string | null
  /**
   * Pickup should be within this radius of the provided pickup city/state.
   */
  pickupMiles?: number | null
  /**
   * If no `pickupEndDate` is given, the pickup must occur on (not after) this date.
   */
  pickupStartDate?: string | null
  /**
   * Only used in conjunction with `pickupStartDate`. Pickup must occur on or before this date.
   */
  pickupEndDate?: string | null
  /**
   * Array of integers representing ISO-8601 days of week (i.e., Monday = 1 thru Sunday = 7). Pickup must be on one of these days of the week. Used when there is no specific `pickup{Start,End}Date`.
   */
  pickupIsoDaysOfWeek?: number[] | null
  deliveryCity?: string | null
  deliveryStateCode?: string | null
  /**
   * Delivery should be within this radius of the provided delivery city/state.
   */
  deliveryMiles?: number | null
  /**
   * If no `deliveryEndDate` is given, the delivery must occur on (not after) this date.
   */
  deliveryStartDate?: string | null
  /**
   * Only used in conjunction with `deliveryStartDate`. Delivery must occur on or before this date.
   */
  deliveryEndDate?: string | null
  /**
   * Corresponds to the Marketplace "equipment key" (`equipment.key` in the MP DB.)
   */
  trailerType?: string | null
  /**
   * Used in conjunction with `loadedMilesMax`. Total loaded distance must be in this range, if provided.
   */
  loadedMilesMin?: number | null
  /**
   * Used in conjunction with `loadedMilesMin`. Total loaded distance must be in this range, if provided.
   */
  loadedMilesMax?: number | null
  /**
   * BE will default to false if `null` is provided.
   */
  dropTrailer?: boolean | null
  /**
   * Subscribe all of this carrier's dispatchers
   */
  subscribeEveryone?: boolean | null
  /**
   * Parent IDs of subscriber dispatchers.
   */
  subscriberParentIds?: number[]
  rate?: number | null
  rateType?: '' | 'all_in' | 'rpm' | null
  bookableShipmentSavedSearchSubscribersAttributes?: {
    parentUserId: number
    hasNotificationsEnabled: boolean
    destroy?: boolean
  }[]
}

export interface ErrorResponse {
  errors: string[]
}

export interface GetFMSLaneAlertDetailsPathParams {
  /**
   * Carrier ID
   */
  carrierId: number
}

export type GetFMSLaneAlertDetailsProps = Omit<
  GetProps<LaneAlertResponse, ErrorResponse | void, void, GetFMSLaneAlertDetailsPathParams>,
  'path'
> &
  GetFMSLaneAlertDetailsPathParams

/**
 * Get FMS lane alert details
 */
export const GetFMSLaneAlertDetails = ({ carrierId, ...props }: GetFMSLaneAlertDetailsProps) => (
  <Get<LaneAlertResponse, ErrorResponse | void, void, GetFMSLaneAlertDetailsPathParams>
    path={`/v9/carriers/${carrierId}/lane_alerts`}
    {...props}
  />
)

export type UseGetFMSLaneAlertDetailsProps = Omit<
  UseGetProps<LaneAlertResponse, ErrorResponse | void, void, GetFMSLaneAlertDetailsPathParams>,
  'path'
> &
  GetFMSLaneAlertDetailsPathParams

/**
 * Get FMS lane alert details
 */
export const useGetFMSLaneAlertDetails = ({
  carrierId,
  ...props
}: UseGetFMSLaneAlertDetailsProps) =>
  useGet<LaneAlertResponse, ErrorResponse | void, void, GetFMSLaneAlertDetailsPathParams>(
    (paramsInPath: GetFMSLaneAlertDetailsPathParams) =>
      `/v9/carriers/${paramsInPath.carrierId}/lane_alerts`,
    { pathParams: { carrierId }, ...props }
  )

export interface CreateLaneAlertPathParams {
  /**
   * Carrier ID
   */
  carrierId: number
}

export interface CreateLaneAlertRequestBody {
  bookableShipmentSavedSearch?: BookableShipmentSavedSearch
}

export type CreateLaneAlertProps = Omit<
  MutateProps<void, void, void, CreateLaneAlertRequestBody, CreateLaneAlertPathParams>,
  'path' | 'verb'
> &
  CreateLaneAlertPathParams

/**
 * Create a lane alert on behalf of a carrier manager
 */
export const CreateLaneAlert = ({ carrierId, ...props }: CreateLaneAlertProps) => (
  <Mutate<void, void, void, CreateLaneAlertRequestBody, CreateLaneAlertPathParams>
    verb='POST'
    path={`/v9/carriers/${carrierId}/lane_alerts`}
    {...props}
  />
)

export type UseCreateLaneAlertProps = Omit<
  UseMutateProps<void, void, void, CreateLaneAlertRequestBody, CreateLaneAlertPathParams>,
  'path' | 'verb'
> &
  CreateLaneAlertPathParams

/**
 * Create a lane alert on behalf of a carrier manager
 */
export const useCreateLaneAlert = ({ carrierId, ...props }: UseCreateLaneAlertProps) =>
  useMutate<void, void, void, CreateLaneAlertRequestBody, CreateLaneAlertPathParams>(
    'POST',
    (paramsInPath: CreateLaneAlertPathParams) =>
      `/v9/carriers/${paramsInPath.carrierId}/lane_alerts`,
    { pathParams: { carrierId }, ...props }
  )

export interface DeleteCMLaneAlertPathParams {
  /**
   * Carrier ID
   */
  carrierId: number
}

export type DeleteCMLaneAlertProps = Omit<
  MutateProps<void, ErrorResponse, void, string, DeleteCMLaneAlertPathParams>,
  'path' | 'verb'
> &
  DeleteCMLaneAlertPathParams

/**
 * Delete CM lane alert
 */
export const DeleteCMLaneAlert = ({ carrierId, ...props }: DeleteCMLaneAlertProps) => (
  <Mutate<void, ErrorResponse, void, string, DeleteCMLaneAlertPathParams>
    verb='DELETE'
    path={`/v9/carriers/${carrierId}/lane_alerts`}
    {...props}
  />
)

export type UseDeleteCMLaneAlertProps = Omit<
  UseMutateProps<void, ErrorResponse, void, string, DeleteCMLaneAlertPathParams>,
  'path' | 'verb'
> &
  DeleteCMLaneAlertPathParams

/**
 * Delete CM lane alert
 */
export const useDeleteCMLaneAlert = ({ carrierId, ...props }: UseDeleteCMLaneAlertProps) =>
  useMutate<void, ErrorResponse, void, string, DeleteCMLaneAlertPathParams>(
    'DELETE',
    (paramsInPath: DeleteCMLaneAlertPathParams) =>
      `/v9/carriers/${paramsInPath.carrierId}/lane_alerts`,
    { pathParams: { carrierId }, ...props }
  )

export interface UpdateCMLaneAlertPathParams {
  /**
   * Carrier ID
   */
  carrierId: number
  /**
   * Lane Alert ID
   */
  id: number
}

export interface UpdateCMLaneAlertRequestBody {
  bookableShipmentSavedSearch?: BookableShipmentSavedSearch
}

export type UpdateCMLaneAlertProps = Omit<
  MutateProps<void, ErrorResponse, void, UpdateCMLaneAlertRequestBody, UpdateCMLaneAlertPathParams>,
  'path' | 'verb'
> &
  UpdateCMLaneAlertPathParams

/**
 * Update CM lane alert
 */
export const UpdateCMLaneAlert = ({ carrierId, id, ...props }: UpdateCMLaneAlertProps) => (
  <Mutate<void, ErrorResponse, void, UpdateCMLaneAlertRequestBody, UpdateCMLaneAlertPathParams>
    verb='PUT'
    path={`/v9/carriers/${carrierId}/lane_alerts/${id}`}
    {...props}
  />
)

export type UseUpdateCMLaneAlertProps = Omit<
  UseMutateProps<
    void,
    ErrorResponse,
    void,
    UpdateCMLaneAlertRequestBody,
    UpdateCMLaneAlertPathParams
  >,
  'path' | 'verb'
> &
  UpdateCMLaneAlertPathParams

/**
 * Update CM lane alert
 */
export const useUpdateCMLaneAlert = ({ carrierId, id, ...props }: UseUpdateCMLaneAlertProps) =>
  useMutate<void, ErrorResponse, void, UpdateCMLaneAlertRequestBody, UpdateCMLaneAlertPathParams>(
    'PUT',
    (paramsInPath: UpdateCMLaneAlertPathParams) =>
      `/v9/carriers/${paramsInPath.carrierId}/lane_alerts/${paramsInPath.id}`,
    { pathParams: { carrierId, id }, ...props }
  )
